import {
	wallFinishLook,
	cableItemById,
	defaultPipe,
	getCatalogItem,
	getCeilingMaterial,
	getFloorLayout,
	getFloorMaterial,
	getMaterial,
	getScreedMaterial,
	getWaterproofMaterial,
	pipeItemById,
} from "@/shared/catalog";
import { pointInPolygon } from "@/shared/lib/geometry";
import { LayerKind, wallFaceKey, type CatalogRef, type PlanLayers } from "@/shared/plan";
import {
	cableRoute,
	detectRooms,
	fixturePlanPoint,
	openingsOfWall,
	pipeRoute,
	roomTitle,
	roomWallFaces,
	wallLength,
	type Room,
} from "@/widgets/plan";
import { exportObjectMarks } from "./exportMarks";

export interface PrintLegendItem {
	id?: string;
	label: string;
	detail: string;
}

export interface PrintLegendSection {
	title: string;
	items: PrintLegendItem[];
}

interface RoomLegendItem extends PrintLegendItem {
	roomId: string;
}

type LegendCollector = (
	layers: PlanLayers,
	rooms: Room[],
	marks: ReadonlyMap<string, string>,
) => RoomLegendItem[];

const roomForPoint = (rooms: Room[], point: { x: number; y: number }) =>
	rooms.find((room) => pointInPolygon(point, room.inner))?.id;

const groupedItems = (items: RoomLegendItem[]) => {
	const groups = new Map<
		string,
		{ roomId: string; id?: string; label: string; detail: string; count: number }
	>();
	for (const item of items) {
		const key = `${item.roomId}-${item.id ?? ""}-${item.label}-${item.detail}`;
		const group = groups.get(key);
		if (group) group.count += 1;
		else {
			groups.set(key, {
				roomId: item.roomId,
				id: item.id,
				label: item.label,
				detail: item.detail,
				count: 1,
			});
		}
	}
	return [...groups.values()].map((group) => ({
		roomId: group.roomId,
		id: group.id,
		label: group.label,
		detail: group.count > 1 ? `${group.detail} · ${group.count} шт.` : group.detail,
	}));
};

const wallsLegendItems: LegendCollector = (layers, rooms) =>
	rooms.flatMap((room) =>
		[...new Set(room.wallIds)].flatMap((wallId) => {
			const wall = layers.walls.walls[wallId];
			const material = getMaterial(wall?.material);
			if (!wall) return [];
			return [
				{
					roomId: room.id,
					label: `Стена: ${material.name}`,
					detail: `${wall.bearing ? "несущая" : "перегородка"} · длина ${Math.round(wallLength(layers.walls, wall))} мм · толщина ${wall.thickness} мм · высота ${wall.height} мм`,
				},
				...openingsOfWall(layers.walls, wall.id).map((opening) => ({
					roomId: room.id,
					label: opening.kind === "door" ? "Проём: дверь" : "Проём: окно",
					detail: `${opening.width} × ${opening.height} мм${opening.sill > 0 ? ` · отметка ${opening.sill} мм` : ""}`,
				})),
			];
		}),
	);

const floorsLegendItems: LegendCollector = (layers) =>
	Object.values(layers.floors.items).map((floor) => {
		const material = getFloorMaterial(floor.material);
		const layout = getFloorLayout(floor.layout);
		return {
			roomId: floor.roomId,
			label: `Пол: ${material.name}`,
			detail: `${layout.label} · ${floor.width ?? material.tile.width} × ${floor.length ?? material.tile.length} мм`,
		};
	});

const ceilingLegendItems: LegendCollector = (layers) =>
	Object.values(layers.ceiling.items).map((ceiling) => {
		const material = getCeilingMaterial(ceiling.material);
		return {
			roomId: ceiling.roomId,
			label: `Потолок: ${material.name}`,
			detail: `опуск ${ceiling.drop ?? material.drop} мм`,
		};
	});

const screedLegendItems: LegendCollector = (layers) =>
	Object.values(layers.screed.items).map((screed) => ({
		roomId: screed.roomId,
		label: `Стяжка: ${getScreedMaterial(screed.material).name}`,
		detail: "",
	}));

const waterproofLegendItems: LegendCollector = (layers) =>
	Object.values(layers.waterproof.items).map((waterproof) => ({
		roomId: waterproof.roomId,
		label: `Гидроизоляция: ${getWaterproofMaterial(waterproof.material).name}`,
		detail: "",
	}));

const catalogLegendItems = (
	roomId: (point: { x: number; y: number }) => string | undefined,
	items: { id: string; ref: CatalogRef; x: number; y: number }[],
	marks: ReadonlyMap<string, string>,
): RoomLegendItem[] =>
	items.flatMap((item) => {
		const catalogItem = getCatalogItem(item.ref);
		const room = roomId(item);
		return catalogItem && room
			? [
					{
						roomId: room,
						id: marks.get(item.id),
						label: catalogItem.name,
						detail: `${catalogItem.width} × ${catalogItem.depth} мм`,
					},
				]
			: [];
	});

const itemLayerLegendItems =
	(layer: "furniture" | "plumbing" | "electrical"): LegendCollector =>
	(layers, rooms, marks) => {
		const layerData = layers[layer];
		const roomId = (point: { x: number; y: number }) => roomForPoint(rooms, point);
		const floorItems = catalogLegendItems(roomId, Object.values(layerData.items), marks);
		const fixtures = Object.values(layerData.fixtures).flatMap((fixture) => {
			const point = fixturePlanPoint(layers.walls, fixture);
			const catalogItem = getCatalogItem(fixture.ref);
			const room = point && catalogItem ? roomId(point) : undefined;
			return room && catalogItem
				? [
						{
							roomId: room,
							id: marks.get(fixture.id),
							label: catalogItem.name,
							detail: `${catalogItem.width} × ${catalogItem.height} мм`,
						},
					]
				: [];
		});
		return [...floorItems, ...fixtures];
	};

const furnitureLegendItems = itemLayerLegendItems("furniture");
const plumbingLegendItems = itemLayerLegendItems("plumbing");
const electricalLegendItems = itemLayerLegendItems("electrical");

const wiringLegendItems: LegendCollector = (layers, rooms) => {
	return Object.values(layers.wiring.items).flatMap((wiring) => {
		const cable = wiring.material ? cableItemById(wiring.material) : null;
		const route = cableRoute(layers.walls, layers.electrical, wiring);
		const point = route?.[Math.floor(route.length / 2)];
		const roomId = point && roomForPoint(rooms, point);
		return roomId
			? [{ roomId, label: `Кабель: ${cable?.name ?? "марка не указана"}`, detail: "" }]
			: [];
	});
};

const pipesLegendItems: LegendCollector = (layers, rooms) =>
	Object.values(layers.pipes.items).flatMap((pipe) => {
		const route = pipeRoute(layers.walls, layers.plumbing, pipe);
		const point = route?.[Math.floor(route.length / 2)];
		const roomId = point && roomForPoint(rooms, point);
		const material = pipe.material ? (pipeItemById(pipe.material) ?? defaultPipe()) : defaultPipe();
		return roomId
			? [{ roomId, label: `Труба: ${material.name}`, detail: `${material.diameter} мм` }]
			: [];
	});

const roomsLegendItems: LegendCollector = () => [];

/** Отделка стен: по комнате — что лежит на её стенах, по разу на каждый вид. */
const finishesLegendItems: LegendCollector = (layers, rooms) =>
	rooms.flatMap((room) =>
		roomWallFaces(layers.walls, room).flatMap((face) => {
			const finish = layers.finishes.items[wallFaceKey(face.wallId, face.side)];
			const look = finish && wallFinishLook(finish);
			return look
				? [{ roomId: room.id, label: `Отделка: ${look.item.name}`, detail: look.color }]
				: [];
		}),
	);

const legendCollectors: Record<LayerKind, LegendCollector> = {
	[LayerKind.Walls]: wallsLegendItems,
	[LayerKind.Floors]: floorsLegendItems,
	[LayerKind.Waterproof]: waterproofLegendItems,
	[LayerKind.Rooms]: roomsLegendItems,
	[LayerKind.Ceiling]: ceilingLegendItems,
	[LayerKind.Wiring]: wiringLegendItems,
	[LayerKind.Electrical]: electricalLegendItems,
	[LayerKind.Plumbing]: plumbingLegendItems,
	[LayerKind.Furniture]: furnitureLegendItems,
	[LayerKind.Screed]: screedLegendItems,
	[LayerKind.Pipes]: pipesLegendItems,
	[LayerKind.WallFinish]: finishesLegendItems,
	// Комментарий сам себе подпись — в легенду листа не попадает
	[LayerKind.Comments]: () => [],
	// Подложка на лист не идёт — в легенде ей нечего показать
	[LayerKind.Underlay]: () => [],
	[LayerKind.Baseboard]: () => [],
};

/** Строит только те секции легенды, которые явно выбраны для листа экспорта. */
export const exportLegend = (
	legendLayers: readonly LayerKind[] | undefined,
	layers: PlanLayers,
) => {
	if (!legendLayers) return undefined;
	const rooms = detectRooms(layers.walls);
	const items = groupedItems(
		legendLayers.flatMap((layer) =>
			legendCollectors[layer](layers, rooms, exportObjectMarks(layers, legendLayers)),
		),
	);
	const sections = rooms
		.map((room, index) => ({
			title: roomTitle(layers.rooms, room.id, index),
			items: items.filter((item) => item.roomId === room.id),
		}))
		.filter((section) => section.items.length > 0);
	return sections.length > 0 ? sections : undefined;
};
